import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// 30 second timeout for ALL requests. Long-running stage runs are now async
// (returns 202/200 with job_id) so no legitimate request needs >30s. This
// prevents a hung connection from looking like a stuck UI.
const api = axios.create({ baseURL: API, timeout: 30000 });

// Unauthenticated axios — used by /share/* (PublicReview) so a stale token
// in the logged-in user's localStorage can never corrupt a public request.
export const publicApi = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("aivos_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err?.response?.status;
        if (status === 401) {
            localStorage.removeItem("aivos_token");
            localStorage.removeItem("aivos_user");
            // Never redirect the public share / review page to /login — those
            // pages are meant to be viewed in incognito by clients without
            // any app credentials.
            const p = window.location.pathname;
            const isPublic = p.startsWith("/login") || p.startsWith("/review")
                || p.startsWith("/share") || p.startsWith("/public");
            if (!isPublic) {
                window.location.href = "/login";
            }
        }
        const detail = err?.response?.data?.detail;

        // 403 with `account_pending` = the active-user gate kicked in (e.g.
        // admin demoted us mid-session). Hard-redirect to /pending so the
        // user can never reach the gated UI.
        if (status === 403 && detail && typeof detail === "object" && detail.code === "account_pending") {
            err.userMessage = detail.message || "Your account is awaiting admin approval.";
            if (!window.location.pathname.startsWith("/pending") &&
                !window.location.pathname.startsWith("/login") &&
                !window.location.pathname.startsWith("/profile")) {
                window.location.href = "/pending";
            }
            return Promise.reject(err);
        }

        // 402 with a structured payload = quota gate from /api/clients or
        // /api/projects. Broadcast a global event so the QuotaModal can
        // render a clean upgrade prompt regardless of which page triggered
        // the request.
        if (status === 402 && detail && typeof detail === "object" &&
                (detail.code === "quota_exceeded" || detail.code === "no_plan")) {
            try {
                window.dispatchEvent(new CustomEvent("aivos:quota", { detail }));
            } catch { /* DOM unavailable (SSR/tests) */ }
            err.userMessage = detail.message || "Plan limit reached.";
            return Promise.reject(err);
        }

        // Phase N.4 — friendly classification for the universally-useless
        // "Network Error" / timeout / aborted / no-response scenarios.
        // These usually mean: the request never reached the backend OR the
        // backend response was killed by the ingress layer. Either way the
        // job may STILL be running; the UI should reflect that hopefully.
        const isTransport = !err?.response && (
            err?.code === "ECONNABORTED" ||
            err?.message === "Network Error" ||
            err?.message?.toLowerCase?.()?.includes("timeout") ||
            err?.message?.toLowerCase?.()?.includes("aborted")
        );

        // Patch D (Phase N.8) — turn ANY shape of detail/error into a
        // human-readable string. Handles: string, FastAPI validation array
        // [{loc,msg,type}], plain object with .message/.detail/.error,
        // generic object (JSON.stringify so we never render
        // `[object Object]`).
        const _stringify = (v) => {
            if (v == null) return "";
            if (typeof v === "string") return v;
            if (Array.isArray(v)) {
                // FastAPI validation: array of {loc, msg, type, ...}.
                return v.map((x) =>
                    typeof x === "string" ? x
                        : (x?.msg || x?.message || JSON.stringify(x))
                ).join("; ");
            }
            if (typeof v === "object") {
                return v.message || v.detail || v.error
                    || (() => {
                        try { return JSON.stringify(v); }
                        catch { return "Request failed."; }
                    })();
            }
            return String(v);
        };

        // Normalise error so callers can show err.userMessage cleanly
        err.userMessage =
            (typeof detail === "string" && detail) ||
            (status === 402 && "Emergent Universal Key budget exhausted. Top up via Profile → Universal Key → Add Balance.") ||
            (status === 409 && (typeof detail === "object" ? _stringify(detail) : detail) || (status === 409 && "Already running. Wait for it to finish or click Cancel.")) ||
            (status === 502 && "AI provider is temporarily unavailable (502). Please retry.") ||
            (status === 503 && "AI provider is temporarily unavailable (503). Please retry.") ||
            (status === 504 && "Request timed out. Please retry.") ||
            (status >= 500 && "Server error. Please retry.") ||
            (isTransport && (
                err?.code === "ECONNABORTED" || (err?.message || "").includes("timeout")
                    ? "Request took too long and was cancelled. The action may still be running — refresh in a few seconds to see the latest state."
                    : "Connection issue. Please check your internet and retry. (If you just clicked Run/Retry, the action may already be queued — wait a few seconds and refresh.)"
            )) ||
            _stringify(detail) ||
            err.message ||
            "Request failed.";
        // Final guard — if anything above produced something non-string,
        // coerce safely. This is the last line of defence against
        // `[object Object]` ever reaching a toast.
        if (typeof err.userMessage !== "string") {
            err.userMessage = _stringify(err.userMessage) || "Request failed.";
        }
        return Promise.reject(err);
    },
);

export default api;
export { BACKEND_URL };
